import { useWorkspaceInfoAiUser } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoAiUser';
import { Button, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { ChatChannelType } from 'src/@types/chat';
import { IWorkspaceUser } from 'src/@types/workspace-info';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useVoiceChannelContext } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { createChannel } from 'src/redux/slices/chat';
import { dispatch, useSelector } from 'src/redux/store';

export const CallAI = () => {
  const [loading, setLoading] = useState(false);
  const [lockButton, setLockButton] = useState(false);
  const [isInCall, setIsInCall] = useState(false);

  const { translate } = useLocales();

  const { activeVoiceChannel } = useLivekitRTCState();
  const { connectAiCall, disconnectVoiceChannel } = useVoiceChannelContext();

  const { aiUser } = useWorkspaceInfoAiUser();

  const channelsMap = useSelector((state) => state.chat.conversations.byId);

  const aiChat = Object.values(channelsMap).find(
    (chat) => chat.p2p && chat.chatPartner?.user === aiUser?._id,
  );

  const handleCreateAIChat = async () => {
    if (!aiUser) return;
    await dispatch(
      createChannel({
        type: ChatChannelType.TEXT,
        p2p: true,
        name: aiUser.name,
        participants: [aiUser],
        onSuccess: () => {
          handleCallAi();
        },
      }),
    );
  };

  const handleCallAi = async () => {
    if (!aiUser || (!isInCall && lockButton)) return null;
    if (isInCall) {
      disconnectVoiceChannel();
      return;
    }
    setLoading(true);
    if (!aiChat) {
      await handleCreateAIChat();
      return;
    }
    setLockButton(true);
    setIsInCall(true);
    connectAiCall({
      aiCallId: aiChat._id,
      aiCallTitle: aiUser.name,
      aiUser: aiUser as IWorkspaceUser,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (isInCall || !lockButton) return;
    setTimeout(() => setLockButton(false), 250);
  }, [isInCall, lockButton]);

  useEffect(() => {
    setIsInCall(activeVoiceChannel?.channel?._id === aiChat?._id);
  }, [activeVoiceChannel?.channel?._id, aiChat?._id]);

  const tooltip = isInCall
    ? translate('actions_bar.call_ai_chat_in_call')
    : lockButton
      ? translate('actions_bar.call_ai_chat_locked')
      : translate('actions_bar.call_ai_chat');

  return (
    <Tooltip title={tooltip} arrow placement="bottom">
      <Button
        sx={{ fontWeight: 'fontWeightRegular' }}
        startIcon={<Iconify icon="tabler:phone" />}
        onClick={() => handleCallAi()}
        disabled={isInCall || lockButton}
      >
        Call
      </Button>
    </Tooltip>
  );
};
